import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Loader } from 'lucide-react';
import { doc, getDoc } from 'firebase/firestore';
import { IngredientForm } from './IngredientForm';
import { UsdaSuggestModal } from './unitConversion/UsdaSuggestModal';
import { useStandardIngredientsStore } from '../../store/useStandardIngredientsStore';
import { StandardIngredient, IngredientStatus } from '../../types/ingredients';
import { db } from '../../lib/firebase';
import { suggestUsdaOnSave } from '../../utils/unitConversionApi';
import type { UsdaPortionCandidate } from '../../types/unitConversion';

function getEffectiveStatus(ingredient: StandardIngredient): IngredientStatus {
  return ingredient.status === 'pending' ? 'pending' : 'validated';
}

/**
 * Dedicated page for creating / editing a standard ingredient.
 * Routes:
 *   /admin/ingredients/new
 *   /admin/ingredients/:id/edit
 */
export function IngredientFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isCreate = !id;
  const { ingredients, addIngredient, updateIngredient, processImage, fetchIngredients } =
    useStandardIngredientsStore();

  const [ingredient, setIngredient] = useState<StandardIngredient | undefined>();
  const [loading, setLoading] = useState(!isCreate);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [suggest, setSuggest] = useState<{
    ingredientId: string;
    ingredientName: string;
    nameEn?: string | null;
    candidates: UsdaPortionCandidate[];
  } | null>(null);
  const [suggestingUsda, setSuggestingUsda] = useState(false);

  useEffect(() => {
    if (isCreate) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    (async () => {
      setLoading(true);
      setError(null);

      const fromStore = ingredients.find((i) => i.id === id);
      if (fromStore) {
        if (!cancelled) {
          setIngredient(fromStore);
          setLoading(false);
        }
        return;
      }

      if (ingredients.length === 0) {
        await fetchIngredients();
      }
      const afterFetch = useStandardIngredientsStore
        .getState()
        .ingredients.find((i) => i.id === id);
      if (afterFetch) {
        if (!cancelled) {
          setIngredient(afterFetch);
          setLoading(false);
        }
        return;
      }

      try {
        const snap = await getDoc(doc(db, 'standardIngredients', id!));
        if (!snap.exists()) {
          if (!cancelled) setError('Ingrédient introuvable.');
        } else if (!cancelled) {
          setIngredient({ id: snap.id, ...snap.data() } as StandardIngredient);
        }
      } catch (err) {
        if (!cancelled) setError((err as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [id, isCreate]); // eslint-disable-line react-hooks/exhaustive-deps

  const goBack = () => navigate('/admin/ingredients');

  const maybeSuggestUsda = async (
    ingredientId: string,
    name: string,
    preferredUnit: string,
    unitWeight_g?: number | null
  ) => {
    if (preferredUnit !== 'piece') return false;
    if (typeof unitWeight_g === 'number' && unitWeight_g > 0) return false;
    try {
      const res = await suggestUsdaOnSave(ingredientId);
      if (res.suggestion.needed) {
        setSuggest({
          ingredientId,
          ingredientName: name,
          nameEn: res.suggestion.nameEn,
          candidates: res.suggestion.candidates || [],
        });
        return true;
      }
    } catch (err) {
      console.warn('USDA suggest-on-save failed:', err);
    }
    return false;
  };

  const handleSubmit = async (
    data: Omit<StandardIngredient, 'id' | 'createdAt' | 'updatedAt'>
  ) => {
    setError(null);
    setInfo(null);
    try {
      if (isCreate) {
        const newId = await addIngredient(data);
        const shown = await maybeSuggestUsda(
          newId,
          String(data.name || ''),
          String(data.preferredUnit || ''),
          data.unitWeight_g
        );
        if (!shown) navigate('/admin/ingredients');
        return;
      }
      if (!ingredient) return;

      const wasPending = getEffectiveStatus(ingredient) === 'pending';
      await updateIngredient({
        ...ingredient,
        ...data,
        ...(wasPending ? { status: 'validated' } : {}),
      });

      const shown = await maybeSuggestUsda(
        ingredient.id,
        String(data.name || ingredient.name),
        String(data.preferredUnit || ingredient.preferredUnit),
        data.unitWeight_g ?? ingredient.unitWeight_g
      );
      if (shown) return;

      if (wasPending) {
        setInfo(`«${ingredient.name}» a été validé.`);
        setTimeout(() => navigate('/admin/ingredients'), 600);
      } else {
        navigate('/admin/ingredients');
      }
    } catch (err) {
      setError((err as Error).message || 'Échec de l’enregistrement');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-24">
        <Loader className="h-10 w-10 animate-spin text-menioo-green" />
      </div>
    );
  }

  return (
    <div className="space-y-4 max-w-3xl">
      <div className="flex items-center gap-3">
        <Link
          to="/admin/ingredients"
          className="inline-flex items-center text-sm text-gray-500 hover:text-menioo-navy"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Ingrédients
        </Link>
        <span className="text-gray-300">/</span>
        <h2 className="text-xl font-display font-bold text-menioo-navy">
          {isCreate
            ? 'Nouvel ingrédient'
            : getEffectiveStatus(ingredient!) === 'pending'
              ? `Valider — ${ingredient?.name || ''}`
              : `Modifier — ${ingredient?.name || ''}`}
        </h2>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm">
          {error}
          <button type="button" onClick={goBack} className="ml-3 underline">
            Retour à la liste
          </button>
        </div>
      )}

      {info && (
        <div className="rounded-md bg-green-50 border border-green-200 text-green-800 px-4 py-3 text-sm">
          {info}
        </div>
      )}

      {!error && (isCreate || ingredient) && (
        <IngredientForm
          key={ingredient?.id || 'new'}
          ingredient={ingredient}
          onSubmit={handleSubmit}
          onCancel={goBack}
          processImage={processImage}
          suggestingUsda={suggestingUsda}
          onSuggestUsda={
            ingredient?.id &&
            ['piece', 'gram', 'kilogram'].includes(ingredient.preferredUnit) &&
            !(typeof ingredient.unitWeight_g === 'number' && ingredient.unitWeight_g > 0)
              ? async () => {
                  setSuggestingUsda(true);
                  setError(null);
                  try {
                    const shown = await maybeSuggestUsda(
                      ingredient.id,
                      ingredient.name,
                      ingredient.preferredUnit,
                      ingredient.unitWeight_g
                    );
                    if (!shown) {
                      setInfo('Aucun candidat USDA à proposer pour le moment.');
                    }
                  } finally {
                    setSuggestingUsda(false);
                  }
                }
              : undefined
          }
        />
      )}

      {suggest && (
        <UsdaSuggestModal
          ingredientId={suggest.ingredientId}
          ingredientName={suggest.ingredientName}
          nameEn={suggest.nameEn}
          candidates={suggest.candidates}
          onClose={() => {
            setSuggest(null);
            navigate('/admin/ingredients');
          }}
          onApplied={() => {
            setSuggest(null);
            navigate('/admin/ingredients');
          }}
        />
      )}
    </div>
  );
}
